/**
 * High-level hooks for proposal list operations
 */

import {
  hashKey,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { isPrefetchDebugEnabled, prefetchDebug } from '@/lib/prefetch/debug';

import type { FilterTag, ProposalListFilters } from '../core/types';
import {
  proposalListInfiniteQuery,
  proposalListQuery,
  proposalsByResolutionQuery,
  proposalsByTagQuery,
} from '../query/queries';

/**
 * Standard page size used across all proposal list queries.
 * This ensures all components share the same cache entry.
 *
 * Must stay <= the backend proposals-list pagination cap
 * (ActionsListPagination.max_page_size in apps/api/pagination.py); a larger value
 * is silently clamped by DRF and truncates the list. Enforced by the backend
 * guard test apps/api/tests/test_pagination.py.
 */
export const PROPOSALS_PAGE_SIZE = 500;

type ListQueryOptions = ReturnType<typeof proposalListQuery>;

/**
 * Hook for listing proposals with optional filters
 *
 * @param orgId - Organization ID
 * @param filters - Optional filters
 * @param options - Additional query options
 * @returns Query result with proposals list
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useProposals(orgId, {
 *   resolution: 'pending',
 * });
 *
 * return (
 *   <div>
 *     {data?.results.map(proposal => (
 *       <div key={proposal.id}>{proposal.title}</div>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useProposals(
  orgId: number,
  filters?: ProposalListFilters,
  options?: Omit<ListQueryOptions, 'queryKey' | 'queryFn'>
) {
  const queryOpts = proposalListQuery(orgId, filters);
  return useQuery({
    ...queryOpts,
    ...options,
  });
}

/**
 * Hook for getting pending proposals assigned to the current user
 *
 * Uses the standard PROPOSALS_PAGE_SIZE to ensure cache sharing with
 * useMyPendingProposalsPaginated. This eliminates duplicate API requests
 * when multiple components need proposal data.
 *
 * @param orgId - Organization ID
 * @param options - Additional query options (staleTime, enabled, etc.)
 * @returns Query result with user's pending proposals
 *
 * @example
 * ```typescript
 * const { data } = useMyPendingProposals(orgId);
 * const count = data?.count ?? 0;
 * ```
 */
export function useMyPendingProposals(
  orgId: number,
  options?: { staleTime?: number; enabled?: boolean }
) {
  // Use the same query key as useMyPendingProposalsPaginated to share cache
  return useQuery({
    ...proposalListQuery(orgId, {
      resolution: 'pending',
      assigned_to_me: true,
      page: 1,
      page_size: PROPOSALS_PAGE_SIZE,
    }),
    ...options,
  });
}

/**
 * Hook for getting pending proposals with pagination support
 *
 * @param orgId - Organization ID
 * @param page - Current page number (1-indexed)
 * @param pageSize - Number of items per page
 * @param filterTag - Optional filter tag to filter by category
 * @param options - Additional query options including:
 *   - staleTime: Cache duration in ms
 *   - enabled: Whether to run the query
 *   - includeRecipientData: Include expensive recipient data (decryption + CRM lookups).
 *     Only enable for small lists that need contact/company data. Default false.
 * @returns Query result with paginated proposals
 *
 * @example
 * ```typescript
 * const { data } = useMyPendingProposalsPaginated(orgId, 1, 20);
 * const totalPages = Math.ceil((data?.count ?? 0) / 20);
 *
 * // With filter:
 * const { data } = useMyPendingProposalsPaginated(orgId, 1, 20, 'email');
 *
 * // With recipient data (for small lists needing contact/company info):
 * const { data } = useMyPendingProposalsPaginated(orgId, 1, 10, undefined, {
 *   includeRecipientData: true,
 * });
 * ```
 */
export function useMyPendingProposalsPaginated(
  orgId: number,
  page: number = 1,
  pageSize: number = PROPOSALS_PAGE_SIZE,
  filterTag?: FilterTag,
  options?: {
    staleTime?: number;
    enabled?: boolean;
    includeRecipientData?: boolean;
  }
) {
  const listOpts = proposalListQuery(orgId, {
    resolution: 'pending',
    assigned_to_me: true,
    page,
    page_size: pageSize,
    filter_tags__contains: filterTag,
    include_recipient_data: options?.includeRecipientData,
  });
  const qc = useQueryClient();
  if (isPrefetchDebugEnabled()) {
    const hookKeyHash = hashKey(listOpts.queryKey);
    const existingEntry = qc.getQueryCache().get(hookKeyHash);
    prefetchDebug('useMyPendingProposalsPaginated mount', {
      hookKey: listOpts.queryKey,
      hookKeyHash,
      queryClientId: (qc as unknown as Record<string, unknown>).__debugId,
      cacheHit: !!existingEntry,
      existingStatus: existingEntry?.state.status,
      existingFetchStatus: existingEntry?.state.fetchStatus,
      existingDataUpdatedAt: existingEntry?.state.dataUpdatedAt,
      page,
      pageSize,
      filterTag,
      includeRecipientData: options?.includeRecipientData,
    });
  }
  return useQuery({
    ...listOpts,
    // Keep previous data visible while fetching new filter results,
    // preventing filter tabs from disappearing during transitions
    placeholderData: keepPreviousData,
    ...options,
  });
}

/**
 * Hook for a single resolved resolution (Done/Dismissed/Archived history),
 * page-numbered. One scalar `resolution` per call preserves the free
 * WebSocket cache reconciliation and keeps the nav-badge (resolution:'pending')
 * cache uncontaminated.
 */
export function useMyResolvedProposalsPaginated(
  orgId: number,
  resolution: 'approved' | 'rejected' | 'withdrawn',
  page: number,
  pageSize: number,
  options?: { enabled?: boolean; staleTime?: number }
) {
  const listOpts = proposalListQuery(orgId, {
    resolution,
    assigned_to_me: true,
    page,
    page_size: pageSize,
  });
  return useQuery({
    ...listOpts,
    placeholderData: keepPreviousData,
    ...options,
  });
}

/**
 * Infinite-scroll variant of {@link useMyResolvedProposalsPaginated} for the
 * Done/Dismissed/Archived history lists: pages accumulate as the user scrolls
 * rather than replacing each other. Same one-scalar-resolution-per-call rule
 * (keeps the nav-badge pending cache uncontaminated).
 */
export function useMyResolvedProposalsInfinite(
  orgId: number,
  resolution: 'approved' | 'rejected' | 'withdrawn',
  pageSize: number,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    ...proposalListInfiniteQuery(orgId, {
      resolution,
      assigned_to_me: true,
      page_size: pageSize,
    }),
    ...options,
  });
}

/**
 * Hook for getting proposals by resolution status
 *
 * @param orgId - Organization ID
 * @param resolution - Resolution status
 * @param options - Additional query options
 * @returns Query result with filtered proposals
 *
 * @example
 * ```typescript
 * const { data: approved } = useProposalsByResolution(orgId, 'approved');
 * const { data: rejected } = useProposalsByResolution(orgId, 'rejected');
 * ```
 */
export function useProposalsByResolution(
  orgId: number,
  resolution: 'pending' | 'approved' | 'rejected' | 'withdrawn',
  options?: Omit<ListQueryOptions, 'queryKey' | 'queryFn'>
) {
  const queryOpts = proposalsByResolutionQuery(orgId, resolution);
  return useQuery({
    ...queryOpts,
    ...options,
  });
}

/**
 * Hook for getting proposals by filter tag
 *
 * @param orgId - Organization ID
 * @param tag - Filter tag
 * @param options - Additional query options
 * @returns Query result with tagged proposals
 *
 * @example
 * ```typescript
 * const { data: emailProposals } = useProposalsByTag(orgId, 'email');
 * const { data: pipelineUpdates } = useProposalsByTag(orgId, 'pipeline_update');
 * ```
 */
export function useProposalsByTag(
  orgId: number,
  tag: 'email' | 'pipeline_update' | 'research',
  options?: Omit<ListQueryOptions, 'queryKey' | 'queryFn'>
) {
  const queryOpts = proposalsByTagQuery(orgId, tag);
  return useQuery({
    ...queryOpts,
    ...options,
  });
}

/**
 * Convenience hook for getting only email proposals
 *
 * @param orgId - Organization ID
 * @param options - Additional query options
 * @returns Query result with email proposals
 *
 * @example
 * ```typescript
 * const { data } = useEmailProposals(orgId);
 * ```
 */
export function useEmailProposals(
  orgId: number,
  options?: Omit<ListQueryOptions, 'queryKey' | 'queryFn'>
) {
  return useProposalsByTag(orgId, 'email', options);
}

/**
 * Convenience hook for getting pending proposals
 *
 * @param orgId - Organization ID
 * @param options - Additional query options
 * @returns Query result with pending proposals
 *
 * @example
 * ```typescript
 * const { data, isLoading } = usePendingProposals(orgId);
 * ```
 */
export function usePendingProposals(
  orgId: number,
  options?: Omit<ListQueryOptions, 'queryKey' | 'queryFn'>
) {
  return useProposalsByResolution(orgId, 'pending', options);
}
